import { buildOrgDesignResult, computeBudget } from "./orgDesignTreeEngine";
import { isAbsorptionNewRole } from "./orgDesign.constants";

const generateNewRoleId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `newrole-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Resolves GET /organization-view's roots[] to sb_unique_id(s) within a
// given role set — job_id-safe, same as every other manager reference in
// this module, in case a root's own JD was republished since whichever
// fetch built `jobIdToUniqueId`. Shared by startFromExistingOrg
// (useOrgDesignData.js) and mapOrgRedesignVersionRow below — both resolve
// against their own role set's own job_id map, just fetched from different
// endpoints (today's live current-org-structure vs. a saved version's own
// current_org_structure.roles).
//
// Always returns an ARRAY, possibly empty. An empty result means
// organization-view named no apex role — nothing flagged is_apex_role, the
// fetch failed, or the caller is PAIC and has no such endpoint at all.
// buildOrgDesignResult then roots the tree on every manager-less role, so a
// real role heads the chart either way; no caller ever gets a tree rooted on
// a function.
export const resolveOrgViewRootIds = (organizationView, jobIdToUniqueId) => {
  const roots = organizationView?.roots || [];
  return roots.map((r) => (r.job_id != null && jobIdToUniqueId.get(r.job_id)) || r.sb_unique_id);
};

// Position count as configured in Admin Console > Compensation Management.
// `|| 1` would also override a genuinely vacant/open requisition
// (total_positions: 0) up to 1 — only default when the field is actually
// missing or not a number (a row that predates the field is still one
// requisition).
export const parseTotalPositions = (value) => {
  if (value == null || value === "") return 1;
  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? count : 1;
};

// /current-org-structure rows carry the role's configured position count
// (total_positions) but no compensation/AI-impact fields, so those default to
// 0 until a real comp source is wired in.
//
// `reportsTo.sb_unique_id` goes stale the moment that manager's JD is
// republished (new sb_unique_id, same job_id) — `jobIdToUniqueId` (built by
// the caller from this same fetch) resolves through the stable job_id
// instead, falling back to the pointer's own sb_unique_id only when this
// fetch has no row for that job_id.
export const mapCurrentOrgStructureRole = (row, jobIdToUniqueId = new Map()) => {
  const reportsTo = row.role_reporting_to;
  const isObjectRef = reportsTo && typeof reportsTo === "object";
  const managerJobId = isObjectRef ? reportsTo.job_id : null;
  const resolvedManagerId =
    (managerJobId != null && jobIdToUniqueId.get(managerJobId)) || (isObjectRef ? reportsTo.sb_unique_id : reportsTo) || null;
  return {
    sb_unique_id: row.sb_unique_id,
    job_id: row.job_id,
    manager_id: row.is_apex_role ? null : resolvedManagerId,
    manager_job_id: row.is_apex_role ? null : managerJobId,
    // Kept in case the manager isn't part of this same roles fetch, so
    // "Reports To" can still show a real title instead of going blank.
    manager_job_title: isObjectRef ? reportsTo.job_title : null,
    job_title: row.job_title,
    total_positions: parseTotalPositions(row.total_positions),
    microtask_function: row.function || row.business_unit || "General",
    potential_value_usd: 0,
    annual_compensation: 0,
  };
};

// GET /role-reporting-to/requisition_view rows that aren't already one of
// `roles` (matched on job_id first, then sb_unique_id — the two differ, and
// only job_id survives a JD republish), deduped, as extra "Reports To"
// choices. `entry_id` is the first draft role the row's own reporting chain
// reaches (or null), so callers can rule out a pick that would close a loop.
export const mapExternalManagers = (rows, roles) => {
  const draftIdByJobId = new Map(roles.filter((r) => r.job_id != null).map((r) => [r.job_id, r.sb_unique_id]));
  const draftIds = new Set(roles.map((r) => r.sb_unique_id));
  const validRows = (rows || []).filter((row) => row?.sb_unique_id && row.job_title);
  const rowByJobId = new Map(validRows.filter((row) => row.job_id != null).map((row) => [row.job_id, row]));
  const rowById = new Map(validRows.map((row) => [row.sb_unique_id, row]));

  const toDraftId = (ref) =>
    (ref.job_id != null && draftIdByJobId.get(ref.job_id)) || (draftIds.has(ref.sb_unique_id) ? ref.sb_unique_id : null);

  const findEntryId = (row) => {
    const visited = new Set();
    let current = row;
    while (current && !visited.has(current.sb_unique_id)) {
      visited.add(current.sb_unique_id);
      const raw = current.role_reporting_to;
      const ref = typeof raw === "string" ? { sb_unique_id: raw } : raw;
      if (!ref || typeof ref !== "object") return null;
      const draftId = toDraftId(ref);
      if (draftId) return draftId;
      current = (ref.job_id != null && rowByJobId.get(ref.job_id)) || rowById.get(ref.sb_unique_id);
    }
    return null;
  };

  const seenIds = new Set();
  const seenJobIds = new Set();
  return validRows
    .filter((row) => {
      if (toDraftId(row) || seenIds.has(row.sb_unique_id) || (row.job_id != null && seenJobIds.has(row.job_id))) return false;
      seenIds.add(row.sb_unique_id);
      if (row.job_id != null) seenJobIds.add(row.job_id);
      return true;
    })
    .map((row) => ({
      sb_unique_id: row.sb_unique_id,
      job_id: row.job_id ?? null,
      job_title: row.job_title,
      entry_id: findEntryId(row),
    }));
};

// GET /version/{id}: current_org_structure.roles[] are the "before" facts,
// new_org_structure.roles[] the per-role "after" verdict for those same
// roles, new_org_structure.new_roles[] the AI-proposed additions. Adapted
// into one roles/design/newRoles contract so the tree engine and every
// screen that reads it stay unchanged regardless of data source.
//
// `organizationView` (optional) is the GET /organization-view response for
// a CURRENT ORG-sourced version being reopened — its roots[] resolve to
// `rootIds`, same real-hierarchy tree buildOrgDesignResult builds for a
// fresh "Start from Current Org" (see useOrgDesignData.js's
// startFromExistingOrg/fetchOrgRedesignVersionDraft). Absent for a
// PAIC-sourced row, which has no equivalent endpoint and roots on its own
// manager-less roles instead.
export const mapOrgRedesignVersionRow = (row, organizationView) => {
  const currentRoles = row?.current_org_structure?.roles || [];
  const afterByRoleId = Object.fromEntries((row?.new_org_structure?.roles || []).map((r) => [r.sb_unique_id, r]));
  const newRoles = row?.new_org_structure?.new_roles || [];
  // Same stale-sb_unique_id risk as mapCurrentOrgStructureRole — these rows
  // carry both manager_sb_unique_id and manager_job_id, so resolve through
  // the stable job_id first.
  const jobIdToUniqueId = new Map(currentRoles.map((r) => [r.job_id, r.sb_unique_id]));
  const currentRoleIds = new Set(currentRoles.map((r) => r.sb_unique_id));
  // Shared by roles and new_roles. A manager picked from Role Reporting
  // (outside this version's own roles) comes back with its own title, which
  // is what marks it external — see buildOrgForestFromRealRoots.
  const managerFieldsFor = (r) => {
    const managerId = (r.manager_job_id != null && jobIdToUniqueId.get(r.manager_job_id)) || r.manager_sb_unique_id || null;
    return {
      manager_id: managerId,
      manager_job_id: r.manager_job_id ?? null,
      manager_job_title: r.manager_job_title || null,
      manager_is_external: Boolean(managerId && !currentRoleIds.has(managerId) && r.manager_job_title),
    };
  };

  const roles = currentRoles.map((r) => ({
    sb_unique_id: r.sb_unique_id,
    job_id: r.job_id,
    ...managerFieldsFor(r),
    job_title: r.job_title,
    total_positions: parseTotalPositions(r.total_positions),
    microtask_function: r.function || r.microtask_function || "General",
    // Distinct AI agents selected against this role's own microtasks (see
    // get_agent_counts_by_role) — only ever populated for a PAIC-sourced
    // version; a CURRENT ORG row has no agent selection to count, so this
    // is 0 there.
    agents: r.agents != null ? Number(r.agents) : 0,
    potential_value_usd: Number(r.ai_percentage_c) || 0,
    // Same reasoning as the newRoles annual_cost_usd_per_fte below — `|| 0`
    // can't tell a genuine $0 apart from a value the backend never returned
    // (e.g. stripped for lacking view_comp), so only default when missing.
    annual_compensation: r.annual_compensation != null ? Number(r.annual_compensation) : null,
    // Org Redesign v2 fields live on the "after" row. Absent on a v1 row —
    // defaulted to empty so every reader can treat both versions the same.
    job_level: afterByRoleId[r.sb_unique_id]?.job_level ?? r.job_level ?? "",
    fused_from: afterByRoleId[r.sb_unique_id]?.fused_from || [],
    fusion_rules: afterByRoleId[r.sb_unique_id]?.fusion_rules || [],
  }));

  const design = Object.fromEntries(
    Object.entries(afterByRoleId).map(([roleId, after]) => [
      roleId,
      { status: after.status || "UNCHANGED", after_hc: after.after_hc != null ? Number(after.after_hc) : undefined },
    ])
  );

  const mappedNewRoles = newRoles.map((nr) => ({
    id: generateNewRoleId(),
    role_title: nr.role_title,
    status: nr.status || "CREATED",
    // A brand-new role genuinely starts at 0 headcount — only an edit to a
    // previously-created new role ever carries a real before_hc (see
    // organization_redesign_service._merge_new_roles).
    before_hc: nr.before_hc != null ? Number(nr.before_hc) : 0,
    // An ABSORPTION entry is a merge record, never a seat — forced to 0 so a
    // missing after_hc can't default to 1 and get counted (and costed) as a
    // real new position.
    after_hc: isAbsorptionNewRole(nr) ? 0 : Number(nr.after_hc ?? 1),
    ...managerFieldsFor(nr),
    microtask_function: nr.microtask_function || "General",
    // A missing value here is never guessed at — `computeBudget`/`computeStats`
    // already apply their own 140000 assumption when aggregating, so this
    // adapter only needs to preserve "backend didn't return it" as
    // `undefined` (dropped from any request body entirely) rather than
    // baking in a number, or an explicit null, that could get resaved or
    // resent as if it were real.
    annual_cost_usd_per_fte: nr.annual_cost_usd_per_fte != null ? Number(nr.annual_cost_usd_per_fte) : undefined,
    rationale: nr.rationale || "",
    // Unlike an existing role, a brand-new one has no sb_unique_id/job_desc
    // row yet, so there's no microtask linkage to count distinct agents
    // against — get_agent_counts_by_role (and this schema generally, see
    // organization_redesign_service's new_roles docstrings) has no concept
    // of an agent count for it. null (not 0) so the column can render "—"
    // rather than implying a real zero was measured.
    agents: nr.agents != null ? Number(nr.agents) : null,
    // Org Redesign v2 — only set on a FUSED entry; empty on CREATED and v1.
    fusion_type: nr.fusion_type || "",
    surviving_role_id: nr.surviving_role_id || "",
    job_level: nr.job_level || "",
    replaces: nr.replaces || [],
  }));

  // No branch on source_type: a PAIC row and a CURRENT ORG row whose view
  // fetch failed both arrive with organizationView == null, and both want the
  // same thing — root on the real manager-less roles. They only used to need
  // telling apart because `undefined` selected the function-bucket tree.
  const rootIds = resolveOrgViewRootIds(organizationView, jobIdToUniqueId);

  return {
    roles,
    design,
    newRoles: mappedNewRoles,
    rationale: row?.new_org_structure?.org_design_rationale || "",
    rootIds,
  };
};

// Org Redesign v2: an absorbed role is REDUNDANT like an automated one, but
// its work moved into another role. Maps each absorbed role (by sb_unique_id
// and by job_id — references carry both) to the title of the role it merged
// into: the existing survivor that lists it in fused_from, or the FUSED new
// role that lists it in replaces. A survivor never maps to itself.
export const getMergedIntoTitles = (roles, newRoles) => {
  const mergedInto = new Map();
  const add = (refs, title, survivorId) =>
    (refs || []).forEach((ref) => {
      [ref?.sb_unique_id, ref?.job_id]
        .filter((id) => id && id !== survivorId)
        .forEach((id) => {
          if (!mergedInto.has(id)) mergedInto.set(id, title);
        });
    });
  (roles || []).forEach((r) => add(r.fused_from, r.job_title, r.sb_unique_id));
  (newRoles || []).forEach((nr) => add(nr.replaces, nr.role_title, nr.surviving_role_id));
  return mergedInto;
};

// Org Redesign v2: the fusion fields Save Version sends back
// (PUT /version/{id}/update) so a saved and re-opened version keeps them.
// Only a FUSED new role / a role that absorbed others gets any — every other
// role's payload stays exactly as it was.
export const newRoleFusionSaveFields = (nr) =>
  String(nr?.status ?? "").trim().toUpperCase() === "FUSED"
    ? {
        fusion_type: nr.fusion_type || "",
        surviving_role_id: nr.surviving_role_id || "",
        replaces: nr.replaces || [],
        job_level: nr.job_level || "",
      }
    : {};

export const existingRoleFusionSaveFields = (role) =>
  role?.fused_from?.length ? { fused_from: role.fused_from, fusion_rules: role.fusion_rules || [] } : {};

// Shared by "Generate Org Design" and "open a published version" so both
// land in the exact same draft shape. `result.new_roles` (snake_case) is
// buildOrgDesignResult's own key — every screen reads the camelCase
// `newRoles` instead, so it's set explicitly here too.
// sb_unique_id -> the role's new_org_structure status as the API sent it
// (trimmed, upper-cased; no alias mapping), for screens that
// need the raw value (useOrgDesignData's fetchVersionDraft) — the draft's own
// design statuses are normalized by normalizeDesign.
export const mapApiRoleStatuses = (versionRow) =>
  Object.fromEntries(
    (versionRow?.new_org_structure?.roles || [])
      .filter((role) => role.sb_unique_id)
      .map((role) => [role.sb_unique_id, String(role.status ?? "").trim().toUpperCase()])
  );

export const adaptVersionRowToDraft = (row, organizationView) => {
  const { roles, design, newRoles, rationale, rootIds } = mapOrgRedesignVersionRow(row, organizationView);
  const result = buildOrgDesignResult(roles, design, newRoles, rationale, undefined, rootIds);
  return {
    roles,
    newRoles,
    ...result,
    baseline_budget: computeBudget(roles, {}, []),
    publishedStatus: row?.published_status || null,
    // Carried on the draft (not just used for this one build) so every later
    // edit's recompute() call — see useOrgDesignData.js — keeps rooting on
    // the same apex roles, instead of the chart re-deriving its own top the
    // moment the user makes their first edit.
    orgRootIds: rootIds,
  };
};
